import { AuthContext } from "@modules/auth/contexts/auth-plus.context";
import { useProfiles } from "@modules/profiles/hooks/use-profiles.hook";
import { UserRole } from "@modules/shared/enums/user.enum";
import { useContext, useEffect, useState } from "react";
import {
  FaAndroid,
  FaApple,
  FaCogs,
  FaSave,
  FaSpinner,
} from "react-icons/fa";
import { Navigate } from "react-router-dom";
import { FeatureFlagService } from "../services/feature-flag.service";

interface AppVersionForm {
  iosMinVersion: string;
  androidMinVersion: string;
}

const extractPayload = (
  response: unknown,
): Record<string, unknown> | null => {
  if (!response || typeof response !== "object") return null;
  const root = response as Record<string, unknown>;
  const inner = root.data ?? root.result ?? response;
  if (!inner || typeof inner !== "object" || Array.isArray(inner)) return null;
  return inner as Record<string, unknown>;
};

const pickVersion = (
  response: unknown,
  platform: "ios" | "android",
): string => {
  const data = extractPayload(response);
  if (!data) return "";
  const snake = platform === "ios" ? "ios_min_version" : "android_min_version";
  const camel = platform === "ios" ? "iosMinVersion" : "androidMinVersion";
  const value = data[snake] ?? data[camel];
  return typeof value === "string" ? value : "";
};

function AppVersionForceUpdate() {
  const { auth, setAuth, logout } = useContext(AuthContext);
  const { loadProfile } = useProfiles();

  const [roleReady, setRoleReady] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [formData, setFormData] = useState<AppVersionForm>({
    iosMinVersion: "",
    androidMinVersion: "",
  });
  const [hasConfig, setHasConfig] = useState(false);
  const [initialData, setInitialData] = useState<AppVersionForm>({
    iosMinVersion: "",
    androidMinVersion: "",
  });

  useEffect(() => {
    const checkRole = async () => {
      try {
        const me = await loadProfile("me");
        setIsSuperAdmin(me?.role === UserRole.SUPER_ADMIN);
      } catch {
        setIsSuperAdmin(false);
      } finally {
        setRoleReady(true);
      }
    };

    checkRole();
  }, [loadProfile]);

  const loadConfig = async () => {
    if (!auth) {
      setError("Authentication required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await FeatureFlagService.getAppVersionConfig(
        auth,
        setAuth,
        logout,
      );
      setHasConfig(extractPayload(response) !== null);
      const next = {
        iosMinVersion: pickVersion(response, "ios"),
        androidMinVersion: pickVersion(response, "android"),
      };
      setFormData(next);
      setInitialData(next);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to load app version config";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (roleReady && isSuperAdmin) {
      loadConfig();
    }
  }, [roleReady, isSuperAdmin, auth]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const validate = (): boolean => {
    if (!formData.iosMinVersion.trim() || formData.iosMinVersion.length > 50) {
      setError("iOS min version is required (1–50 characters).");
      return false;
    }
    if (
      !formData.androidMinVersion.trim() ||
      formData.androidMinVersion.length > 50
    ) {
      setError("Android min version is required (1–50 characters).");
      return false;
    }
    return true;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !auth) return;

    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    try {
      await FeatureFlagService.updateAppVersionConfig(
        auth,
        setAuth,
        logout,
        formData.iosMinVersion.trim(),
        formData.androidMinVersion.trim(),
      );
      const saved = {
        iosMinVersion: formData.iosMinVersion.trim(),
        androidMinVersion: formData.androidMinVersion.trim(),
      };
      setFormData(saved);
      setInitialData(saved);
      setHasConfig(true);
      setSuccessMsg("App version config saved successfully.");
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to save app version config";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const isDirty =
    formData.iosMinVersion !== initialData.iosMinVersion ||
    formData.androidMinVersion !== initialData.androidMinVersion;

  if (!roleReady) {
    return (
      <div className="container py-5 text-center">
        <FaSpinner className="fa-spin text-primary" size={32} />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="d-flex align-items-center gap-3 mb-4">
            <FaCogs size={35} className="text-primary" />
            <div>
              <h1 className="h3 mb-0 fw-bold text-dark">
                App Version — Force Update
              </h1>
              <p className="text-muted small mb-0">
                Reads and updates the{" "}
                <code className="text-dark">APP_MIN_VERSION</code> record in{" "}
                <code className="text-dark">feature_flag</code> — not per-feature
                min versions from Manage Feature.
              </p>
            </div>
          </div>

          {error && (
            <div
              className="alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              <strong>Error:</strong> {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
              />
            </div>
          )}

          {successMsg && (
            <div
              className="alert alert-success alert-dismissible fade show"
              role="alert"
            >
              {successMsg}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMsg(null)}
              />
            </div>
          )}

          {(loading || saving) && (
            <div
              className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{
                backgroundColor: "rgba(255,255,255,0.7)",
                zIndex: 1050,
              }}
            >
              <FaSpinner className="text-primary fa-spin" size={40} />
            </div>
          )}

          {!loading && !hasConfig && (
            <div className="alert alert-warning" role="alert">
              No <code>APP_MIN_VERSION</code> record found yet. Saving will create
              or update it via the API.
            </div>
          )}

          <div className="card shadow-sm border-0">
            <div className="card-body p-4">
              <form onSubmit={handleSave}>
                <div className="row g-4">
                  <div className="col-md-6">
                    <label
                      htmlFor="iosMinVersion"
                      className="form-label small fw-bold d-flex align-items-center gap-2"
                    >
                      <FaApple className="text-dark" />
                      iOS Minimum Version
                    </label>
                    <input
                      id="iosMinVersion"
                      type="text"
                      className="form-control"
                      name="iosMinVersion"
                      value={formData.iosMinVersion}
                      onChange={handleChange}
                      placeholder="e.g. 1.4.0"
                      required
                    />
                    <div className="form-text">
                      Apps below this version on iOS must update.
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label
                      htmlFor="androidMinVersion"
                      className="form-label small fw-bold d-flex align-items-center gap-2"
                    >
                      <FaAndroid className="text-success" />
                      Android Minimum Version
                    </label>
                    <input
                      id="androidMinVersion"
                      type="text"
                      className="form-control"
                      name="androidMinVersion"
                      value={formData.androidMinVersion}
                      onChange={handleChange}
                      placeholder="e.g. 1.3.0"
                      required
                    />
                    <div className="form-text">
                      Apps below this version on Android must update.
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={!isDirty || saving}
                    onClick={() => setFormData(initialData)}
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary d-flex align-items-center gap-2"
                    disabled={!isDirty || saving}
                  >
                    <FaSave />
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppVersionForceUpdate;
